import "./components/media-preview.js";
import './components/item-view.js';
import './components/item-list-view.js';
import './components/item-search-result.js';
import './components/loading-placeholder.js';
import './components/provide-value.js';
import './components/edit-container.js';
import './components/edit-attribute.js';
import './jslib/list-component.js';
import './jslib/state-component.js';
import './jslib/state-element-hook.js';
import './jslib/single-select.js';
import './jslib/multi-select.js';
import './components/attribute-multi-select.js';
import { AttachmentKey, AttributeKey, AttributeKeyData, GlobalState, globalStateKey, idKey, IDType, Immutable, isLoadingPlaceholder, isLocal, Item, ItemAttribute, ItemAttributeKey, ItemIDKey, ItemMutateFn, LoadingPlaceholder } from "./globalstate.js";
import { collectState, createState, proxyState, readonly, State, transformState } from "./jslib/state.js";
import * as schema from "./schema.js";
import { assert, cameraPicker, fireAndForget, on_init } from "./jslib/util.js";
import { bindElement, registerElementTransformFunction } from "./jslib/state-bind.js";
import { provideInherited, provideState, retrieveInherited } from "./jslib/inherited-state.js";
import { registerServerErrorHandlers, UnsavedChangesToken } from "./util.js";
import { registerToastErrorHandlers, showToast } from "./toast.js";
import { SchemaProvider, schemaTypes, listOf, eventuallyCheck, SchemaType, json_get, json_post, check } from "./schema-types.js";
import { Caption, captionRegistry, captionRegistryKey } from "./caption-registry.js";
import AttributeSelect, { attributeKeyRegistryKey } from "./components/attribute-select.js";
import { AddAttachmentMutator, AddItemAttributeMutator, AddItemMutator, DeleteAttachmentMutator, DeleteItemMutator, fromJSON, ItemPropertyUpdate, ModifyItemAttributeValueMutator, MoveItemMutator, ReorderAttachmentsMutator, ReorderContentsMutator, RemoveItemAttributeMutator } from "./mutators.js";
import { enrol, refreshSession, AuthError } from "./auth.js";
import { showSearchModal } from "./search-modal.js";
import { showMoveItemModal } from "./move-item-modal.js";

export type ItemUpdateHooks = {
  [K in keyof Item]?: (value: Item[K], previous: Item[K]) => ItemMutateFn;
}

const SESSION_KEY = 'crates:new-item';

const itemUpdateHooks: ItemUpdateHooks = {
  name: (value) => new ItemPropertyUpdate('name', value),
  description: (value) => new ItemPropertyUpdate('description', value),
  comment: (value) => new ItemPropertyUpdate('comment', value),
  displayChildAttributes: (value) => new ItemPropertyUpdate('displayChildAttributes', value),
  attachments: (value, previous) => {
    const added = value.filter((attachment) => !previous.some((a) => a.id === attachment.id));
    const removed = previous.filter((attachment) => !value.some((a) => a.id === attachment.id));
    // Make sure we're only reordering, not adding/removing.
    assert(added.length === 0 && removed.length === 0, "Attachments must be added/removed using the provided action listeners");
    return new ReorderAttachmentsMutator(value.map((a) => a.id));
  },
  containedIDs: (value, previous) => {
    const added = value.filter((id) => !previous.some((prevID) => idKey(prevID) === idKey(id)));
    const removed = previous.filter((id) => !value.some((prevID) => idKey(prevID) === idKey(id)));
    // Make sure we're only reordering, not adding/removing.
    assert(added.length === 0 && removed.length === 0, "Contained items must be added/removed using the provided action listeners");
    return new ReorderContentsMutator(value);
  },
  attributes: (value, previous) => {
    const changed: ModifyItemAttributeValueMutator[] = [];
    for (const attr of value) {
      const prev = previous.find((a) => idKey(a.id) === idKey(attr.id));
      assert(prev !== undefined, "Attribute value must be added/removed using the provided action listeners");
      assert(prev.keyId === attr.keyId, "Attribute key cannot be changed");
      if (prev.value !== attr.value)
        changed.push(new ModifyItemAttributeValueMutator(attr.id, attr.value));
    }

    assert(changed.length === 1, "Expected exactly one attribute value change at a time");
    return changed[0];
  }
};

/*
function itemHooks(id: IDType): WriteHooks<Item> {
  const hooks: WriteHooks<Item> = {};
  for (const [k, h] of Object.entries(itemUpdateHooks)) {
    const key = k as keyof Item;
    const hook = h as (value: Item[typeof key]) => MutateFn<Item>;

    hooks[key] = (value: Item[typeof key]) => {
      const mutator = hook(value);
      globalState.mutateItem(id, mutator);
    };
  }
  return hooks;
}
*/



function parseJSONElement<T>(schema: SchemaType<T> | SchemaProvider<T>, id: string): T | undefined {
  const text = document.getElementById(id)?.textContent;
  if (!text)
    return undefined;

  try { 
    const data = JSON.parse(text); 
    if ('validate' in schema)
      return schema.assertValid(data);
    
    return eventuallyCheck(schema, data);
  }
  catch (err) { 
    console.log(`Error parsing JSON block element: {id}`, err);
    return undefined;
  }
}

// (id, state) => interceptWrites(state, itemHooks(id))

const preloadedItemsRaw = parseJSONElement(listOf(schemaTypes.Item), 'items-data') ?? [];
const preloadedItems = new Map<string, Immutable<schema.Item>>();
for (const schemaItem of preloadedItemsRaw)
  preloadedItems.set(schemaItem.id, schemaItem);

async function fetchItem(id: string): Promise<Immutable<schema.Item>> {
  // See if it forms part of the preloaded data
  const preloaded = preloadedItems.get(id);
  if (preloaded) {
    preloadedItems.delete(id);
    return preloaded;
  }

  const data = await json_get(schemaTypes.Item, `/item/${id}`);
  return data;
}

function buildGlobalState(): GlobalState {
  const preloadedAttributeKeys = parseJSONElement(listOf(schemaTypes.AttributeKey), 'attribute-keys-data') ?? [];
  const rootIDs = parseJSONElement(listOf(schemaTypes.ItemID), 'root-item-ids-data') ?? [];

  const attributeKeys = new Map<string, AttributeKeyData>();
  for (const key of preloadedAttributeKeys)
    attributeKeys.set(key.id, { name: key.name });
    
  return new GlobalState({
    rootIDs,
    items: new Map(),
    attributeKeys,
  }, fetchItem);
}

const globalState = buildGlobalState();

const unsavedChanges = new UnsavedChangesToken(false);

globalState.addListener((state) => {
  unsavedChanges.state = (state.history.length > 0);
});

function offerRestoreDraft(draft: schema.Mutator[]) {
  function restoreDraft() {
    for (const m of draft)
      globalState.mutate(fromJSON(m));
    clearDraft();
    showToast('Changes restored', 'success');
  }

  const restoreToast = showToast('Unsaved changes from a previous session', 'info', { actions: [
    {
      label: 'Restore',
      onClick: () => {
        restoreDraft();
        restoreToast.dismiss();
      }
    },
    {
      label: 'Dismiss',
      onClick: () => {
        clearDraft();
        restoreToast.dismiss();
      }
    }
  ]});
}

provideInherited(document.body, captionRegistryKey, captionRegistry);
provideInherited(document.body, globalStateKey, globalState);
provideState(document.body, attributeKeyRegistryKey, globalState.attributeKeys);

on_init(() => {
  function bindSingleItem(id: IDType) {
    const state = globalState.getItem(id);
    const itemState = hookItemUpdates(state);
    bindElement(document.body, itemState);
    provideInherited(document.body, ItemIDKey, id);

    const displayKeysState = itemState.select('displayChildAttributes');

    function displayedAttributeKeys(idsState: State<IDType[]>) {
      return transformState(
        (ids: IDType[], keys: AttributeKey[]) =>
          ids.map(id => keys.find(k => idKey(k.id) === idKey(id)))
            .filter((k): k is AttributeKey => k !== undefined),
        (newKeys: AttributeKey[], _ids: IDType[], existingKeys: AttributeKey[]) =>
          [newKeys.map(k => k.id), existingKeys] as const,
        idsState,
        globalState.attributeKeys
      );
    }

    function displayedAttributes(childItemState: State<Item>) {
      return transformState(
        (displayKeys: IDType[], attributes: ItemAttribute[]) =>
          displayKeys.map(keyId => ({
            keyId,
            values: attributes.filter(attr => idKey(attr.keyId) === idKey(keyId))
          })),
        null,
        displayKeysState,
        childItemState.select('attributes')
      );
    }

    registerElementTransformFunction('displayedAttributeKeys', displayedAttributeKeys, document.body);    
    registerElementTransformFunction('displayedAttributes', displayedAttributes, document.body);
  }

  // Okay, what states do we need to set up?
  const id = document.body.dataset.itemId;
  const createItemCode = document.body.dataset.createItemCode;
  const newItemTransfer = sessionStorage.getItem(SESSION_KEY);

  if (id) {
    // We've got a single, existing item.
    bindSingleItem(id);
  }
  else if (createItemCode) {
    // We're creating a new (root) item with the given code.
    const mutator = new AddItemMutator(null, createItemCode);
    globalState.mutate(mutator);
    bindSingleItem(mutator.id);

    globalState.mutateItem(mutator.id, new ItemPropertyUpdate('name', `Crate ${createItemCode}`));
  }
  else if (newItemTransfer) {
    sessionStorage.removeItem(SESSION_KEY);

    async function loadSession(sessionData: string) {
      try {
        const mutationData = await check(listOf(schemaTypes.Mutator), JSON.parse(sessionData));
        const mutators = mutationData.map(fromJSON);

        let id: IDType | null = null;
        for (const mutator of mutators) {
          globalState.mutate(mutator);
          if (mutator instanceof AddItemMutator)
            id = mutator.id;
        }

        if (!id)
          throw new Error('No item created in session data');
        
        bindSingleItem(id);
      }
      catch (e) {
        console.error('Failed to restore new item state', e);
      }
    }

    fireAndForget(loadSession(newItemTransfer));
  }
  else {
  //if (rawRootIDs !== undefined) {
    // We've got a list of root IDs to load.
    bindElement(document.body, collectState( {rootIDs: globalState.rootIDs} ));
  //}
  }

  async function loadSavedDraft() {
    const draft = await loadDraft();
    if (draft)
      offerRestoreDraft(draft);
  }

  fireAndForget(loadSavedDraft());
});

const itemPlaceholderData: Omit<Item, 'id'> = {
  name: "Loading...",
  parent: null,
  description: null,
  attachments: [],
  attributes: [],
  code: null,
  comment: null,
  containedIDs: [],
  displayChildAttributes: []
};

function hookItemUpdates(state: State<Item | LoadingPlaceholder>): State<Item> {
  const forceItem = state.partial((value) => {
    if (!isLoadingPlaceholder(value))
      return value;

    // Return a dummy item for the loading placeholder, so that the update hooks can be used even while loading.
    return {
      id: value.id,
      ...itemPlaceholderData
    };
  });

  function applyHook<K extends keyof Item>(key: K, state: State<Item>): State<Item[K]> {
    const hook = itemUpdateHooks[key];
    if (!hook)
      return state.select(key);

    return state.partial((value) => value[key], (update, current) => {
      const mutator = hook(update, current[key]);
      globalState.mutateItem(current.id, mutator);
      return current;
    });
  }

  const itemKeys = [
    'id', 'name', 'parent', 'description', 'attachments', 'attributes',
    'code', 'comment', 'containedIDs', 'displayChildAttributes'
  ] as const;

  const states = Object.fromEntries(itemKeys.map((key) => [key, applyHook(key, forceItem)])) as {
    [K in keyof Item]: State<Item[K]>;
  };

  return collectState(states);
}

function loadItem(id: State<IDType | null>): State<Item> {
  const initial: State<Item | LoadingPlaceholder> = createState(globalState.placeholder(id.data ?? { localID: 'invalid-id' }));
  const state = proxyState(initial);

  id.listen((newID) => {
    if (newID)
      state.rebind(globalState.getItem(newID));
  });

  return hookItemUpdates(state);
}

type RootItemPlaceholder = {
  name: string;
};

type LinkOnlyItem = {
  id: IDType;
  name: string;
};

type LinkableItem = Item | RootItemPlaceholder | LinkOnlyItem;

const rootItemPlaceholder: RootItemPlaceholder = { name: 'Home' };

function itemToLinkable(item: State<Item | LoadingPlaceholder>): State<LinkableItem> {
  return item.partial((item) => {
    if (isLoadingPlaceholder(item))
      return { id: item.id, name: 'Loading...' };

    return { id: item.id, name: item.name };
  });
}

function loadLinkable(id: State<IDType | null>): State<LinkableItem> {
  function inner(id: IDType | null): State<LinkableItem> {
    if (!id)
      return createState(rootItemPlaceholder);

    return itemToLinkable(globalState.getItem(id));
  }

  const state = proxyState(inner(id.data));

  id.listen((newID) => {
    if (newID)
      state.rebind(inner(newID));
  });

  return readonly(state);
}

function loadAncestorChain(id: IDType | null): State<LinkableItem[]> {
  if (!id)
    return createState([rootItemPlaceholder]);

  const itemState = globalState.getItem(id);
  const parentChainProxy = proxyState<LinkableItem[]>(createState([]));
  let lastParentKey: string | null | undefined = undefined;

  function onItemUpdate(item: Item | LoadingPlaceholder) {
    const newParent = isLoadingPlaceholder(item) ? null : (item.parent ?? null);
    const newKey = newParent ? idKey(newParent) : null;
    if (newKey === lastParentKey)
      return;

    lastParentKey = newKey;
    parentChainProxy.rebind(loadAncestorChain(newParent));
  }

  itemState.listen(onItemUpdate);
  onItemUpdate(itemState.data);

  return transformState(
    (chain: LinkableItem[], self: LinkableItem) => [...chain, self],
    null,
    parentChainProxy,
    itemToLinkable(itemState)
  );
}

function loadAncestors(parentIdState: State<IDType | null>): State<LinkableItem[]> {
  const item = loadItem(parentIdState);
  const parent = item.select('parent');

  const state = proxyState(loadAncestorChain(parent.data));

  parent.listen((newID) => {
    state.rebind(loadAncestorChain(newID));
  });

  return readonly(state);
}

// registerElementTransformFunction('update-hooks', hookItemUpdates);
registerElementTransformFunction('loadItem', loadItem);
registerElementTransformFunction('linkable', loadLinkable);
registerElementTransformFunction('loadAncestors', loadAncestors);
registerElementTransformFunction('isEmpty', (state: State<any>) => state.partial((arr) => Array.isArray(arr) ? arr.length === 0 : !arr));
registerElementTransformFunction('nonEmpty', (state: State<any>) => state.partial((arr) => Array.isArray(arr) ? arr.length > 0 : !!arr));
registerElementTransformFunction('not', (state: State<boolean>) => state.partial((b) => !b));
registerElementTransformFunction('first', (state: State<any[]>) => state.partial((arr) => arr[0] ?? null));
registerElementTransformFunction('length', (state: State<any[]>) => state.partial((arr) => arr.length));
registerElementTransformFunction('qrURL', (state: State<string | null>) => state.partial((str) => str ? `/qr/${encodeURIComponent(str)}` : null));
registerElementTransformFunction('percent', (state: State<number>) => state.partial((num) => `${Math.round(num * 100)}%`));

registerElementTransformFunction('getAttributeName', (state: State<IDType>) =>
  transformState(
    (keyId: IDType, keys: AttributeKey[]) => keys.find((k) => idKey(k.id) === idKey(keyId))?.name ?? '(unknown)',
    null,
    state,
    globalState.attributeKeys
  )
);

registerElementTransformFunction('editUrl', (state: State<LinkableItem | IDType>): State<string> => {
  return state.partial((item) => {
    if (typeof item === 'object') {
      if (!('id' in item))
        return '/'; 
      item = item.id;
    }
    
    if (isLocal(item))
      return '';

    return `/${item}`;
  });
});

registerToastErrorHandlers();
registerServerErrorHandlers();

function getDraftKey(): string {
  return `crates:draft:${location.pathname}`;
}

export function saveDraft(payload: schema.Mutator[]): void {
  try {
    localStorage.setItem(getDraftKey(), JSON.stringify(payload));
  }
  catch(e) {
    // best-effort: ignore storage failures
    console.log('Failed to save draft:', e);
  }
}

export async function loadDraft(): Promise<schema.Mutator[] | null> {
  try {
    const raw = localStorage.getItem(getDraftKey());
    if (!raw)
      return null;
    
    return await check(listOf(schemaTypes.Mutator), JSON.parse(raw));
  }
  catch(e) {
    // best-effort: ignore storage failures
    console.log('Failed to load draft:', e);
    clearDraft();
    return null;
  }
}

export function clearDraft(): void {
  localStorage.removeItem(getDraftKey());
}

document.body.register_action_listeners({
  'saveChanges': async () => {
    document.body.classList.add('saving');
    const savingToast = showToast('Saving...', 'info', { autodismiss: false });
    try {
      // Send our update history to the server, then reload the page.
      const payload = await Promise.all(globalState.history.map(m => m.toJSON()));
      saveDraft(payload);
      const response = await json_post(listOf(schemaTypes.Item), '/update', payload);
      // We're just gonna ignore the response for now; reload the page instead.
      clearDraft();
      unsavedChanges.unlock();
      if (window.location.pathname === '/new') {
        window.location.href = '/';
      } else {
        location.reload();
      }
    }
    catch (e) {
      document.body.classList.remove('saving');
      savingToast.dismiss();
      showToast('Failed to save changes', 'error');
      throw e;
    }
  },

  'addContainedItem': async function() {
    const parentID = retrieveInherited(this, ItemIDKey);
    if (!parentID)
      return;

    const mutator = new AddItemMutator(parentID);
    globalState.mutate(mutator);
  },

  'addItem': async function() {
    const mutator = new AddItemMutator(null);
    globalState.mutate(mutator);
  },

  'addContainer': async function() {
    const mutator = new AddItemMutator(null);
    globalState.mutate(mutator);
    const mutations = await Promise.all(globalState.history.map(m => m.toJSON()));
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(mutations));

    window.location.href = '/new';
  },

  'addFromPhoto': async function() {
    const parentID = retrieveInherited(this, ItemIDKey);
    if (!parentID)
      return;

    const file = await cameraPicker('image/*');
    if (!file)
      return;

    const mutator = new AddItemMutator(parentID);
    globalState.mutate(mutator);
    const itemID = mutator.id;

    const attachmentMutator = new AddAttachmentMutator(file.name, file);
    globalState.mutateItem(itemID, attachmentMutator);

    // Ask the server to caption the photo, and update the item name/description with the caption when we get it back.
    async function getCaption(file: File, uploadHandle: schema.UploadHandle): Promise<Caption> {
      const request: schema.PhotoCaptionRequest = {      
        name: file.name,
        mimeType: file.type,
        handle: uploadHandle
      }

      const captionResult = await json_post<schema.PhotoCaptionResult, schema.PhotoCaptionRequest>(schemaTypes.PhotoCaptionResult, '/caption', request);

      return { name: captionResult.title, description: captionResult.description ?? null };
    }

    try {
      const uploadHandle = await attachmentMutator.getUploadHandle();
      const caption = await captionRegistry.updateCaption(itemID, getCaption(file, uploadHandle));

      globalState.mutateItem(itemID, new ItemPropertyUpdate('name', caption.name));
      if (caption.description !== null)
        globalState.mutateItem(itemID, new ItemPropertyUpdate('description', caption.description));
    }
    catch (err) {
      // The user probably cancelled the operation, or something went wrong with the captioning.
      // Let's ignore it for now. 
    }
  },

  'removeItem': async function() {
    const itemID = retrieveInherited(this, ItemIDKey);
    if (!itemID)
      return;

    const mutator = new DeleteItemMutator(itemID);
    globalState.mutate(mutator);
  },

  'addAttachment': async function() {
    const itemID = retrieveInherited(this, ItemIDKey);
    if (!itemID)
      return;

    const file = await cameraPicker('image/*');
    if (!file)
      return;

    const mutator = new AddAttachmentMutator(file.name, file);
    globalState.mutateItem(itemID, mutator);
  },

  'addItemAttributeValue': async function() {
    const itemID = retrieveInherited(this, ItemIDKey);
    if (!itemID)
      return;

    const attributeSelect = this.parentElement?.querySelector('attribute-select') as AttributeSelect | null;
    const attributeKey = attributeSelect?.value;
    if (!attributeKey)
      return;

    globalState.mutateItem(itemID, new AddItemAttributeMutator(attributeKey.id, ''));
  },

  'removeItemAttributeValue': async function() {
    const itemID = retrieveInherited(this, ItemIDKey);
    if (!itemID)
      return;

    const attribute = retrieveInherited(this, ItemAttributeKey);
    if (!attribute)
      return;

    globalState.mutateItem(itemID, new RemoveItemAttributeMutator(attribute.id));
  },

  'removeAttachment': async function() {
    const itemID = retrieveInherited(this, ItemIDKey);
    if (!itemID)
      return;

    const attachment = retrieveInherited(this, AttachmentKey);
    if (!attachment)
      return;

    const mutator = new DeleteAttachmentMutator(attachment.id);
    globalState.mutateItem(itemID, mutator);
  },

  'find': function() {
    showSearchModal();
  },

  'moveItem': async function() {
    const itemID = retrieveInherited(this, ItemIDKey);
    if (!itemID)
      return;

    const globalState = retrieveInherited(this, globalStateKey);
    if (!globalState)
      return;

    const itemData = globalState.getItem(itemID).data;
    const itemName = isLoadingPlaceholder(itemData) ? 'this item' : itemData.name;

    const result = await showMoveItemModal(itemID, itemName);
    if (result !== null)
      globalState.mutate(new MoveItemMutator(itemID, result.parentID));
  },
});

async function runAuthAction() {
  const authAction = document.body.dataset.authAction;
  if (!authAction)
    return;

  const statusEl = document.getElementById('status');
  const setStatus = (msg: string) => { if (statusEl) statusEl.textContent = msg; };

  async function authEnrol(): Promise<void> {
    const token = document.body.dataset.token ?? '';
    await enrol(token);
    location.href = '/';
  }

  async function authRefreshSession(): Promise<void> {
    try {
      await refreshSession();
      location.reload();
    }
    catch (err) {
      if (err instanceof AuthError) {
        location.href = '/not-enrolled';
      }
      else {
        throw err;
      }
    }
  }

  const handlers: Record<string, () => Promise<void>> = {
    'enrol': authEnrol,
    'refresh-session': authRefreshSession
  };

  try {
    const handler = handlers[authAction];
    if (!handler) {
      setStatus('Unknown auth action: ' + authAction);
      return;
    }

    await handler();

  }
  catch (err) {
    setStatus('An error occurred: ' + (err instanceof Error ? err.message : String(err)));
  }
}

runAuthAction();